import { useEffect, useRef, useState } from 'react';

import {
	createLeadingAndTrailingRemoteIntentScheduler,
	getInputIntentMultilayer,
} from './get-input-intent';
import {
	type IntentContext,
	type IntentDetectionSource,
	type IntentResponse,
	type MultilayerIntentResponse,
	type ObserveRemoteIntentRequest,
} from './types';

/** Options for {@link useInputIntent}. */
export type UseInputIntentOptions = {
	/** The raw user input to classify. */
	input: string;
	/** Already-resolved values that can affect classification. Locale defaults to `en`. */
	context: IntentContext;
	/** Optional lifecycle observer called only when classification reaches the remote engine. */
	observeRemoteRequest?: ObserveRemoteIntentRequest;
};

/** The latest intent classification accepted by {@link useInputIntent}. */
export type UseInputIntentResult = {
	/** The routing decision, or `null` until the first classification resolves. */
	intent: IntentResponse | null;
	/** The layer that produced `intent`, or `null` while `intent` is `null`. */
	source: IntentDetectionSource | null;
};

/**
 * Returns the latest intent classification accepted for the supplied input and context.
 *
 * Detection is always asynchronous, so `intent` and `source` are both `null` until the first
 * request resolves. While a newer request is unresolved, the last accepted result remains visible.
 * Eligible remote detection runs immediately on the leading edge. Further calls during the active
 * 250 ms Lodash throttle interval are coalesced into one trailing request for the latest input.
 * Superseded pending remote detection is prevented from starting, and the scheduler is cancelled
 * when the component unmounts. Results already in flight are discarded when superseded.
 *
 * Layer selection, errors, fallback, and provenance belong to `getInputIntentMultilayer`;
 * remote engine selection and timeout handling belong to `getInputIntentRemote`. This hook only
 * manages the React request lifecycle.
 */
export const useInputIntent = ({
	input,
	context,
	observeRemoteRequest,
}: UseInputIntentOptions): UseInputIntentResult => {
	const { model, primaryProduct, cloudId, locale = 'en' } = context;
	const [result, setResult] = useState<MultilayerIntentResponse | null>(null);
	const remoteIntentSchedulerRef = useRef<ReturnType<
		typeof createLeadingAndTrailingRemoteIntentScheduler
	> | null>(null);
	if (remoteIntentSchedulerRef.current === null) {
		remoteIntentSchedulerRef.current = createLeadingAndTrailingRemoteIntentScheduler();
	}

	useEffect(() => {
		const remoteIntentScheduler = remoteIntentSchedulerRef.current;

		return () => remoteIntentScheduler?.cancel();
	}, []);
	const observeRemoteRequestRef = useRef(observeRemoteRequest);
	observeRemoteRequestRef.current = observeRemoteRequest;

	useEffect(() => {
		let isCurrent = true;
		const abortController = new AbortController();

		void getInputIntentMultilayer(
			input,
			{
				model,
				primaryProduct,
				cloudId,
				locale,
			},
			{
				observeRemoteRequest: observeRemoteRequestRef.current,
				signal: abortController.signal,
				scheduleRemoteIntent: remoteIntentSchedulerRef.current ?? undefined,
			},
		).then((nextResult) => {
			if (isCurrent) {
				setResult(nextResult);
			}
		});

		return () => {
			isCurrent = false;
			abortController.abort();
		};
	}, [cloudId, input, locale, model, primaryProduct]);

	return {
		intent: result?.intent ?? null,
		source: result?.source ?? null,
	};
};
